const objectPath = require('object-path')
const dateUtils = require('./date')
const formUtils = require('./formUtils')
const currencyUtils = require('./currency')
const utils = require('./utils')

const RETURN_TYPES = {
  number: true,
  boolean: true,
  undefined: true,
  function: true,
}

function renderTemplate(template, context, name = '') {
  if(RETURN_TYPES[typeof(template)]) return template
  if(!template) return
  if(typeof(template) === 'string') {
    try {
      return new Function("return `"+template +"`;").call(context)
    } catch(e) {
      console.error(template)
      throw new Error(`${name} error rendering template: \n\n${e.toString()}\n\n${template}\n\n${e.stack}`)
    }
  }
  else if(utils.isTemplateFunction(template)) {
    try {
      return new Function(template.function).call(context)
    } catch(e) {
      console.error(template)
      throw new Error(`${name} error rendering template: \n\n${e.toString()}\n\n${template.function}}\n\n${e.stack}`)
    }
  }
  else {
    console.error(template)
    throw new Error(`${name} unknown template type: ${typeof(template)}\n\n${JSON.stringify(template)}}`)
  }
}

const Factory = ({
  slot,
  config,
  options = {},
  details = {},
  reference = '',
  createdAt = new Date(),
  ...extra
} = {}) => {

  createdAt = typeof(createdAt) == 'string' ? dateUtils.parseISO(createdAt) : createdAt

  const bookingRecords = slot ? slot.bookingRecords || [] : []
  const slotValue = (name, defaultValue = 0) => objectPath.get(slot, name, defaultValue)
  const configValue = (name, defaultValue = 0) => objectPath.get(config, name, defaultValue)
  const detailValue = (name, defaultValue = '') => objectPath.get(details, name, defaultValue)
  const optionValue = (name, defaultValue = 0) => {
    const value = objectPath.get(options, name, defaultValue)
    if(value === '') return defaultValue
    return value
  }

  const getSlotTemplate = (name) => slot && slot.templates ? slot.templates[name] : ''
  
  const dateTitle = () => dateUtils.title(dateUtils.stringToDate(slot.date))
  const timeTitle = timeRangeTitle = () => formUtils.getTimeRangeTitle(slot)
  
  

  const objectLength = (name, type = 'option') => {
    let baseFunction = optionValue
    if(type == 'detail') baseFunction = detailValue
    else if(type == 'slot') baseFunction = slotValue
    const obj = baseFunction(name, {})
    return Object
      .keys(obj)
      .filter(key => obj[key] ? true : false)
      .length
  }

  const arrayLength = (name, type = 'option') => {
    let baseFunction = optionValue
    if(type == 'detail') baseFunction = detailValue
    else if(type == 'slot') baseFunction = slotValue
    const arr = baseFunction(name, [])
    return arr.length
  }

  // get booking records excluding the reference in the context
  // this is so templates can check if the slot can be booked
  const getBookingRecords = () => bookingRecords

  const getOtherBookingRecords = () => bookingRecords.filter(b => {
    if(!reference) return true
    return b.reference != reference
  })
  
  const isSlotEmpty = () => getOtherBookingRecords().length == 0

  const templateHandler = (templateString, name = '', additionalContext = {}) => renderTemplate(templateString, Object.assign({}, context, additionalContext), name)

  let _prices = null
  const getPrices = () => {
    if(_prices) return _prices
    const template = getSlotTemplate('getPrices')
    let returnPrices = {}
    if(template) {
      returnPrices = templateHandler(template, 'getPrices')
    }
    else {
      returnPrices = slot && config ? formUtils.getSlotPrices(slot, config.prices) : {}
    }
    _prices = returnPrices
    return returnPrices
  }

  const getPrice = name => {
    const prices = getPrices()
    return prices[name] || 0
  }

  const priceTitle = (price) => {
    price = typeof(price) === 'string' ?
      getPrice(price) :
      price
    return currencyUtils.title(configValue('business.currency', 'GBP'), price || 0)
  }

  const canBook = () => {
    if(!config) return false
    if(utils.isSlotBlocked(slot)) return false
    const template = getSlotTemplate('canBook')
    if(!template) return true
    return templateHandler(template, 'canBook')
  }

  let _costs = null
  const getCosts = () => {
    if(_costs) return _costs
    const schema = formUtils.getOptionsSchema({
      config,
      slot,
      template: templateHandler,
    })
    _costs = schema
      .filter(field => {
        return field.calculate || field.auto_calculate_price
      })
      .map(field => {
        // let's a field control what price it uses
        const priceName = field.auto_calculate_price || field.price || field.id
        const price = getPrice(priceName)
        const value = field.getAmount ?
          Math.round(parseInt(templateHandler(field.getAmount, `field.getAmount.${field.id}`))) :
          Math.round(options[field.id])
        const total = field.auto_calculate_price ?
          Math.round(value * price) :
          Math.round(parseInt(templateHandler(field.calculate, `field.calculate.${field.id}`)))
        return {
          id: field.id,
          title: field.title,
          short_title: field.short_title,
          price,
          value,
          total,
        }
      })
      .filter(field => field.total > 0)
    return _costs
  }

  const getLineItems = () => {
    if(!config) return []
    const processLineItemsTemplate = getSlotTemplate('lineItems')
    if(processLineItemsTemplate) {
      if(!utils.isTemplateFunction(processLineItemsTemplate)) throw new Error(`the lineItems template must be a function`)
      return templateHandler(processLineItemsTemplate, 'lineItems').map(item => {
        return Object.assign({}, item, {
          total: Math.round(item.total),
        })
      })
    }
    else {
      return getCosts()
    }
  }

  const getTotals = () => {
    if(!config) {
      return {
        rows: [],
        total: 0,
      }
    }
    const lineItems = getLineItems()
    return {
      rows: lineItems,
      total: Math.round(lineItems.reduce((all, row) => all + row.total, 0)),
    }
  }

  const getTotal = () => getTotals().total
  const getPlatformFee = () => {
    if(!config) return 0
    const template = getSlotTemplate('platformFee')
    if(!template) return 0
    const result = parseFloat(templateHandler(template, 'platformFee'))
    if(isNaN(result)) return 0
    return Math.round(result)
  }
  const getPartnerFee = () => {
    if(!config) return 0
    const template = getSlotTemplate('partnerFee')
    if(!template) return 0
    const result = templateHandler(template, 'partnerFee')
    if(isNaN(result)) return 0
    return Math.round(result)
  }
  // the "getDeposit" function of the config
  // should only return a number based on the booking totals
  // not any fees - this is not the "payNow" which is this plus fees
  const getDeposit = () => {
    if(!config) return getTotal()
    const template = getSlotTemplate('depositAmount')
    if(!template) return getTotal()
    return templateHandler(template, 'depositAmount')
  }
  const getDepositTitle = () => priceTitle(getDeposit())
  const getStripeFee = () => {
    const commissionMode = configValue('payment.stripe_commission_mode', 'default')
    return commissionMode == 'default' ?
      Math.round((getDeposit() * 0.014) + 20) :
      0
  }

  const getBookingFee = () => {
    const stripeFee = getStripeFee()
    const platformFee = getPlatformFee()
    const partnerFee = getPartnerFee()
    return stripeFee + platformFee + partnerFee
  }

  const getPayNow = () => getDeposit() + getBookingFee()
  const getPayNowTitle = () => priceTitle(getPayNow())
  const getBookingFeeTitle = () => priceTitle(getBookingFee())

  const calculateFeePercent = ({
    percent = 0,
    cap = 0,
  }) => {
    let fee = getTotal() * percent
    if(cap > 0 && fee > cap) {
      fee = cap
    }
    return Math.round(fee)
  }

  const getGrandTotal = () => {
    const total = getTotal()
    const fee = getBookingFee()
    return total + fee
  }

  const getCustomerEmail = () => {
    if(!config) return false
    const template = getSlotTemplate('customerEmail')
    if(!template) return ''
    return templateHandler(template, 'customerEmail')
  }

  const slotSummaryData = () => {
    return slot.templates.summaryFields
      // dont show admin fields from inside the template
      .filter(field => {
        if(typeof(field.admin) !== 'boolean') return true
        return field.admin === false
      })
      .map(field => {
        return {
          title: field.title,
          value: templateHandler(field.value, `slot.templates.summaryFields.${field.title}`)
        }
      })
  }

  const slotSummary = () => {
    return slotSummaryData().map(item => `${item.title}: ${item.value}`).join("\n")
  }

  // this is used in email summaries
  const optionsSummaryData = () => {
    const totals = getTotals()
    const lineItems = totals.rows.map(row => {
      const total = currencyUtils.title(config.payment.currency, row.total)
      const amount = row.value
      const price = currencyUtils.title(config.payment.currency, row.price)
      const prefix = amount > 0 ? `${ amount } @ ${ price } = ` : ''
      return {
        title: row.short_title,
        value: `${prefix}${total}`
      }
    })
    return lineItems.concat([{
      title: 'Booking Fee',
      value: currencyUtils.title(config.payment.currency, Math.round(getBookingFee()))
    },{
      title: 'Total',
      value: currencyUtils.title(config.payment.currency, getGrandTotal())
    }])
  }

  const optionsSummary = () => {
    return optionsSummaryData().map(item => `${item.title}: ${item.value}`).join("\n")
  }

  const context = {
    data: {
      slot,
      config,
      options,
      details,
      reference,
      bookingRecords,
    },  
    price: getPrice,
    priceTitle,
    dateTitle,
    timeTitle,
    timeRangeTitle,
    slot: slotValue,
    slotTemplate: getSlotTemplate,
    config: configValue,
    option: optionValue,
    detail: detailValue,
    info: detailValue,
    objectLength,
    arrayLength,
    getBookingRecords,
    getOtherBookingRecords,
    isSlotEmpty,
    template: templateHandler,
    canBook,
    getLineItems,
    getCosts,
    getLineItems,
    getTotals,
    getTotal,
    getGrandTotal,
    getDeposit,
    getPayNow,
    getDepositTitle,
    getPayNowTitle,
    getStripeFee,
    getPlatformFee,
    getPartnerFee,
    getBookingFee,
    getBookingFeeTitle,
    calculateFeePercent,
    getCustomerEmail,
    slotSummaryData,
    slotSummary,
    optionsSummaryData,
    optionsSummary,
    objectPath,
    hasTemplate: name => slot.templates[name] ? true : false,
    reference: () => reference,
    createdAt: () => createdAt,
    createdAtString: () => dateUtils.formatISO(createdAt),
    helpers: Object.keys(config && config.helpers ? config.helpers : {}).reduce((all, name) => {
      all[name] = () => templateHandler(config.helpers[name])
      return all
    }, {}),
    ...extra
  }

  templateHandler.context = context
  templateHandler.run = templateHandler.render = (name) => templateHandler(slot.templates[name], name)
  return templateHandler
}

module.exports = Factory